import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthCallback } from './components/AuthCallback';
import { isGoogleCallback } from './utils/googleAuth';
import { Toaster } from './components/ui/sonner';
import { RouteScrollReset } from './components/RouteScrollReset';

// Public pages
import HomePage from './pages/public/HomePage';
import HowItWorksPage from './pages/public/HowItWorksPage';
import PricingPage from './pages/public/PricingPage';
import FAQPage from './pages/public/FAQPage';
import ContactPage from './pages/public/ContactPage';
import DisclaimerPage from './pages/public/DisclaimerPage';
import PrivacyPage from './pages/public/PrivacyPage';
import TermsPage from './pages/public/TermsPage';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';

// Onboarding
import OnboardingPage from './pages/onboarding/OnboardingPage';

import SampleTaskPage from './pages/public/SampleTaskPage';

// Dashboard pages
import DashboardPage from './pages/dashboard/DashboardPage';
import ChecklistDetailPage from './pages/dashboard/ChecklistDetailPage';
import DocumentsPage from './pages/dashboard/DocumentsPage';
import ProfilePage from './pages/dashboard/ProfilePage';

// Payment pages
import PaymentSuccessPage from './pages/payment/PaymentSuccessPage';
import PaymentCancelledPage from './pages/payment/PaymentCancelledPage';

// Admin pages
import AdminPanelPage from './pages/admin/AdminPanelPage';

// App Router component that handles OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (isGoogleCallback(location)) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/example/sin" element={<SampleTaskPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* Auth routes */}
      <Route path="/login" element={<LoginPage oauthError={location.state?.oauthError} />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Onboarding */}
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        } 
      />

      {/* Dashboard routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/documents" 
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/checklist/:itemId" 
        element={
          <ProtectedRoute>
            <ChecklistDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/documents" 
        element={
          <ProtectedRoute>
            <DocumentsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } 
      />

      {/* Payment routes */}
      <Route 
        path="/payment/success" 
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />

      {/* Admin routes (hidden, no auth check - uses admin key) */}
      <Route path="/admin" element={<AdminPanelPage />} />

      {/* Fallback */}
      <Route path="*" element={<HomePage />} />
    </Routes>
  );
}

// Keep routing outside AuthProvider while observing callbacks before session checks.
const RoutedAuthProvider = ({ children }) => {
  const location = useLocation();
  return <AuthProvider callbackPending={isGoogleCallback(location)}>{children}</AuthProvider>;
};

function App() {
  return (
    <BrowserRouter>
      <RouteScrollReset />
      <LanguageProvider>
        <RoutedAuthProvider>
          <AppRouter />
          <Toaster />
        </RoutedAuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
